import React, { useState, useEffect } from 'react';
import styles from './AddTariffsModal.module.css';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import { FaTimes, FaSave, FaBriefcase, FaSpinner } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';

const truncateText = (text, maxLength = 40) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const AddTariffsModal = ({ show, handleClose, selectedTariff, preselectedCustomer, preselectedProject }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    customer: '',
    projects: [], // Changed from 'project' to 'projects' array
    type: '',
    currency: '',
    date: '',
    info: '',
    isSubcontractor: '',
    hourlyRate: '',
    percentageIncrease: 0,
  });
  const [tariffValues, setTariffValues] = useState([
    { value: '0.0', price: '', selected: false, existingId: null },
    { value: '0.1', price: '', selected: false, existingId: null },
    { value: '0.2', price: '', selected: false, existingId: null },
    { value: '0.3', price: '', selected: false, existingId: null },
    { value: '0.4', price: '', selected: false, existingId: null },
    { value: '0.5', price: '', selected: false, existingId: null },
    { value: '0.6', price: '', selected: false, existingId: null },
    { value: '0.7', price: '', selected: false, existingId: null },
    { value: '0.8', price: '', selected: false, existingId: null },
    { value: '0.9', price: '', selected: false, existingId: null },
    { value: '1.0', price: '', selected: false, existingId: null },
  ]);
  const [defaultPrice, setDefaultPrice] = useState('');
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [existingTariffs, setExistingTariffs] = useState([]);
  const [isLoadingExistingTariffs, setIsLoadingExistingTariffs] =
    useState(false);
  const [existingTariffRecords, setExistingTariffRecords] = useState([]);
  const [projectsWithTariffs, setProjectsWithTariffs] = useState({}); // Track which projects have existing tariffs

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setIsLoadingCustomers(true);
        const customersSnapshot = await getDocs(collection(db, 'Customers'));
        const customersList = customersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCustomers(customersList);
      } catch (error) {
        console.error('Error fetching customers:', error);
        toast.error('Müşteriler yüklenirken bir hata oluştu');
      } finally {
        setIsLoadingCustomers(false);
      }
    };

    if (show) {
      fetchCustomers();
    }
  }, [show]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!formData.customer) {
        setProjects([]);
        return;
      }

      try {
        setIsLoadingProjects(true);
        const projectsSnapshot = await getDocs(collection(db, 'Projects'));
        const selectedCustomer = customers.find(
          (c) => c.id === formData.customer
        );

        if (!selectedCustomer) {
          setProjects([]);
          return;
        }

        const projectsList = projectsSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((project) => project.customer === selectedCustomer.Başlık);

        setProjects(projectsList);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Projeler yüklenirken bir hata oluştu');
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, [formData.customer, customers]);

  useEffect(() => {
    if (selectedTariff) {
      setFormData({
        customer: selectedTariff.customer || '',
        projects: selectedTariff.project ? [selectedTariff.project] : [], // Convert single project to array
        type: selectedTariff.type || '',
        currency: selectedTariff.currency || '',
        date: selectedTariff.date || '',
        info: selectedTariff.info || '',
        isSubcontractor: selectedTariff.isSubcontractor || '',
        hourlyRate: selectedTariff.price || '',
        percentageIncrease: selectedTariff.percentageIncrease || 0,
      });

      // If we're in edit mode and have a selected tariff with FTE value
      if (selectedTariff.type === 'FTE' && selectedTariff.value) {
        setTariffValues((prev) =>
          prev.map((tv) =>
            tv.value === selectedTariff.value ? { ...tv, selected: true } : tv
          )
        );
      } else {
        setTariffValues([
          { value: '0.0', price: '', selected: false, existingId: null },
          { value: '0.1', price: '', selected: false, existingId: null },
          { value: '0.2', price: '', selected: false, existingId: null },
          { value: '0.3', price: '', selected: false, existingId: null },
          { value: '0.4', price: '', selected: false, existingId: null },
          { value: '0.5', price: '', selected: false, existingId: null },
          { value: '0.6', price: '', selected: false, existingId: null },
          { value: '0.7', price: '', selected: false, existingId: null },
          { value: '0.8', price: '', selected: false, existingId: null },
          { value: '0.9', price: '', selected: false, existingId: null },
          { value: '1.0', price: '', selected: false, existingId: null },
        ]);
      }
    } else {
      setFormData({
        customer: preselectedCustomer || '',
        projects: preselectedProject ? [preselectedProject] : [], // Convert to array
        type: '',
        currency: '',
        date: '',
        info: '',
        isSubcontractor: '',
        hourlyRate: '',
        percentageIncrease: 0,
      });
      // Pre-select all FTE values when adding a new tariff
      setTariffValues([
        { value: '0.0', price: '', selected: true, existingId: null },
        { value: '0.1', price: '', selected: true, existingId: null },
        { value: '0.2', price: '', selected: true, existingId: null },
        { value: '0.3', price: '', selected: true, existingId: null },
        { value: '0.4', price: '', selected: true, existingId: null },
        { value: '0.5', price: '', selected: true, existingId: null },
        { value: '0.6', price: '', selected: true, existingId: null },
        { value: '0.7', price: '', selected: true, existingId: null },
        { value: '0.8', price: '', selected: true, existingId: null },
        { value: '0.9', price: '', selected: true, existingId: null },
        { value: '1.0', price: '', selected: true, existingId: null },
      ]);
    }
  }, [selectedTariff, show, preselectedCustomer, preselectedProject]);

  useEffect(() => {
    const fetchExistingTariffs = async () => {
      if (!formData.customer || formData.projects.length === 0) {
        setExistingTariffs([]);
        setExistingTariffRecords([]);
        setProjectsWithTariffs({});
        return;
      }

      setIsLoadingExistingTariffs(true);
      try {
        // Check each selected project for existing tariffs
        const projectTariffMap = {};

        for (const projectId of formData.projects) {
          const tariffsQuery = query(
            collection(db, 'Tariffs'),
            where('customer', '==', formData.customer),
            where('project', '==', projectId)
          );
          const tariffsSnapshot = await getDocs(tariffsQuery);
          const tariffsList = tariffsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Store tariffs for this project
          projectTariffMap[projectId] = tariffsList;
        }

        setProjectsWithTariffs(projectTariffMap);

        // Flatten all tariffs for display purposes
        const allTariffs = Object.values(projectTariffMap).flat();
        setExistingTariffRecords(allTariffs);

        // Update tariffValues to mark existing values as selected (only for single project edit mode)
        if (formData.type === 'FTE' && formData.projects.length === 1) {
          const tariffsList = projectTariffMap[formData.projects[0]] || [];
          const existingFTEValues = tariffsList
            .filter((t) => t.type === 'FTE' && t.active !== false)
            .map((t) => t.value);

          setTariffValues((prev) =>
            prev.map((tv) => {
              const existingTariff = tariffsList.find((t) => t.value === tv.value);
              return {
                ...tv,
                selected: existingFTEValues.includes(tv.value) || tv.selected,
                existingId: existingTariff?.id || null,
                // Mevcut tarifelerin fiyat bilgisini yükle, ama sadece existingId varsa
                price: existingTariff?.id ? (existingTariff.price || '') : tv.price,
              };
            })
          );
        }

        const existingValues = allTariffs.map((t) => t.value);
        existingValues.sort((a, b) => parseFloat(a) - parseFloat(b));
        setExistingTariffs(existingValues);
      } catch (error) {
        console.error('Error fetching existing tariffs:', error);
        toast.error('Mevcut tarifeler yüklenirken bir hata oluştu.');
        setExistingTariffs([]);
        setExistingTariffRecords([]);
        setProjectsWithTariffs({});
      } finally {
        setIsLoadingExistingTariffs(false);
      }
    };

    if (show) {
      fetchExistingTariffs();
    }
  }, [show, formData.customer, formData.projects, formData.type]);

  const handleCustomerChange = (e) => {
    const newCustomer = e.target.value;
    setFormData({
      ...formData,
      customer: newCustomer,
      projects: [], // Reset projects array when customer changes
    });
  };

  const handleProjectToggle = (projectId) => {
    setFormData((prev) => {
      const isSelected = prev.projects.includes(projectId);
      return {
        ...prev,
        projects: isSelected
          ? prev.projects.filter((id) => id !== projectId)
          : [...prev.projects, projectId],
      };
    });
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFormData({
      ...formData,
      type: newType,
    });

    // Reset the tariff values when type changes
    if (newType === 'FTE') {
      // For new tariffs, pre-select all FTE values
      // For editing existing tariffs, keep the current behavior
      setTariffValues((prevValues) =>
        prevValues.map((v) => ({ 
          ...v, 
          price: '', 
          selected: selectedTariff ? false : true // Pre-select all for new tariffs
        }))
      );
    }
  };

  const handleTariffToggle = (index) => {
    if (formData.type !== 'FTE') return;

    const updatedTariffValues = [...tariffValues];
    const wasSelected = updatedTariffValues[index].selected;
    updatedTariffValues[index].selected = !updatedTariffValues[index].selected;
    
    // Eğer FTE değeri seçiliyorsa ve varsayılan fiyat varsa, o fiyatı uygula
    // Hem mevcut tarifelere hem de yeni seçilenlere uygula
    if (!wasSelected && updatedTariffValues[index].selected && defaultPrice) {
      updatedTariffValues[index].price = defaultPrice;
    }
    
    setTariffValues(updatedTariffValues);
  };

  const handleDefaultPriceChange = (e) => {
    const price = e.target.value;
    setDefaultPrice(price);
    
    // Varsayılan fiyat değişikliği sadece yeni seçimlere uygulanır
    // Ama mevcut seçili olanların fiyat alanlarında da gösterilir (görsel olarak)
    if (price) {
      const updatedTariffValues = tariffValues.map(tariff => ({
        ...tariff,
        // Seçili olan ve fiyat alanı boş, 0 veya mevcut varsayılan fiyatla aynı olan değerlere uygula
        price: tariff.selected && (!tariff.price || tariff.price === '0' || tariff.price === 0 || tariff.price === defaultPrice) ? price : tariff.price
      }));
      setTariffValues(updatedTariffValues);
    }
  };

  const handleIndividualPriceChange = (index, price) => {
    const updatedTariffValues = [...tariffValues];
    updatedTariffValues[index].price = price;
    setTariffValues(updatedTariffValues);
  };

  const resetModalState = () => {
    setFormData({
      customer: '',
      projects: [], // Reset to empty array
      type: '',
      currency: '',
      date: '',
      info: '',
      isSubcontractor: '',
      hourlyRate: '',
      percentageIncrease: 0,
    });
    // Pre-select all FTE values for new tariffs
    setTariffValues([
      { value: '0.0', price: '', selected: true, existingId: null },
      { value: '0.1', price: '', selected: true, existingId: null },
      { value: '0.2', price: '', selected: true, existingId: null },
      { value: '0.3', price: '', selected: true, existingId: null },
      { value: '0.4', price: '', selected: true, existingId: null },
      { value: '0.5', price: '', selected: true, existingId: null },
      { value: '0.6', price: '', selected: true, existingId: null },
      { value: '0.7', price: '', selected: true, existingId: null },
      { value: '0.8', price: '', selected: true, existingId: null },
      { value: '0.9', price: '', selected: true, existingId: null },
      { value: '1.0', price: '', selected: true, existingId: null },
    ]);
    setDefaultPrice('');
    setProjectsWithTariffs({}); // Reset projects with tariffs tracking
  };

  const handleModalClose = () => {
    resetModalState();
    handleClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (
        !formData.customer ||
        formData.projects.length === 0 ||
        !formData.type ||
        !formData.currency ||
        !formData.date ||
        !formData.isSubcontractor
      ) {
        toast.error('Lütfen tüm zorunlu alanları doldurun');
        return;
      }

      if (formData.type === 'Saat' && !formData.hourlyRate) {
        toast.error('Lütfen saatlik ücreti girin');
        return;
      }

      if (formData.type === 'FTE') {
        const selectedFTEValues = tariffValues.filter((tv) => tv.selected);
        if (selectedFTEValues.length === 0 && !selectedTariff) {
          // For new tariffs, this check is not needed since all values are pre-selected
          // For editing existing tariffs, we still want this validation
          toast.error('Lütfen en az bir FTE değeri seçin');
          return;
        }
      }

      setIsSaving(true);

      // If editing an existing tariff
      if (selectedTariff) {
        if (formData.type === 'Saat') {
          // Update hourly rate tariff
          await updateDoc(doc(db, 'Tariffs', selectedTariff.id), {
            ...formData,
            price: Number(formData.hourlyRate),
          });
          toast.success('Tarife başarıyla güncellendi');
        } else {
          // For FTE type, handle all selected/unselected values
          let updateCount = 0;
          let createCount = 0;
          let deactivateCount = 0;

          // Process all tariff values
          for (const tariff of tariffValues) {
            if (tariff.selected) {
              if (tariff.existingId) {
                // Update existing tariff
                await updateDoc(doc(db, 'Tariffs', tariff.existingId), {
                  ...formData,
                  value: tariff.value,
                  price: Number(tariff.price) || 0,
                  active: true,
                });
                updateCount++;
              } else {
                // Create new tariff
                await addDoc(collection(db, 'Tariffs'), {
                  ...formData,
                  value: tariff.value,
                  price: Number(tariff.price) || 0,
                  active: true,
                });
                createCount++;
              }
            } else if (tariff.existingId) {
              // Deactivate unselected existing tariff
              await updateDoc(doc(db, 'Tariffs', tariff.existingId), {
                ...formData,
                value: tariff.value,
                price: 0,
                active: false,
              });
              deactivateCount++;
            }
          }

          // Show summary of changes
          if (updateCount > 0) {
            toast.success(`${updateCount} tarife güncellendi`);
          }
          if (createCount > 0) {
            toast.success(`${createCount} yeni tarife eklendi`);
          }
          if (deactivateCount > 0) {
            toast.info(`${deactivateCount} tarife pasif hale getirildi`);
          }
        }
      } else {
        // Creating new tariffs for multiple projects
        let totalCreatedCount = 0;
        let skippedProjects = [];
        const projectsToProcess = formData.projects;

        for (const projectId of projectsToProcess) {
          // Check if this project has existing tariffs
          const projectTariffs = projectsWithTariffs[projectId] || [];
          const hasExistingTariffs = projectTariffs.length > 0;

          if (hasExistingTariffs) {
            // Skip this project and add to warning list
            const project = projects.find((p) => p.id === projectId);
            skippedProjects.push(project?.titleShort || projectId);
            continue;
          }

          // Create tariffs for this project
          if (formData.type === 'Saat') {
            // For "Saat" type, create an entry for each tariff value with the hourly rate
            for (const tariff of tariffValues) {
              const tariffData = {
                ...formData,
                project: projectId, // Use single project ID for each record
                value: tariff.value,
                price: Number(formData.hourlyRate),
                active: true,
              };
              // Remove the projects array from tariffData
              delete tariffData.projects;
              await addDoc(collection(db, 'Tariffs'), tariffData);
              totalCreatedCount++;
            }
          } else {
            // For "FTE" type, only create entries for selected values
            const selectedFTEValues = tariffValues.filter((tv) => tv.selected);
            for (const tariff of selectedFTEValues) {
              const tariffData = {
                ...formData,
                project: projectId, // Use single project ID for each record
                value: tariff.value,
                price: Number(tariff.price) || 0,
                active: true,
              };
              // Remove the projects array from tariffData
              delete tariffData.projects;
              await addDoc(collection(db, 'Tariffs'), tariffData);
              totalCreatedCount++;
            }
          }
        }

        // Show results
        if (totalCreatedCount > 0) {
          toast.success(`${totalCreatedCount} yeni tarife eklendi`);
        }

        if (skippedProjects.length > 0) {
          toast.warning(
            `Aşağıdaki projeler zaten tarifelere sahip olduğu için atlandı: ${skippedProjects.join(', ')}`,
            { autoClose: 5000 }
          );
        }

        if (totalCreatedCount === 0 && skippedProjects.length === 0) {
          toast.info('Eklenecek yeni tarife bulunamadı');
        }
      }

      resetModalState();
      handleClose();
    } catch (error) {
      console.error('Error adding/updating tariffs:', error);
      toast.error('Tarifeler işlenirken bir hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className={`${styles.modalBackdrop} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.modal} ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <h2 className={styles.title}>
            <FaBriefcase className={styles.headerIcon} />
            {selectedTariff ? 'Tarife Düzenle' : 'Yeni Tarifeler Ekle'}
          </h2>
          <button
            onClick={handleModalClose}
            className={styles.closeIcon}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        <div
          className={`${styles.formContainer} ${isDarkMode ? 'dark-mode' : ''}`}
        >
          <form onSubmit={handleSubmit}>
            <div className={styles.twoColumnLayout}>
              <div className={styles.leftColumn}>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Müşteri</label>
                    <select
                      value={formData.customer}
                      onChange={handleCustomerChange}
                      className={styles.select}
                      required
                      disabled={isLoadingCustomers}
                    >
                      <option value="">Lütfen Seçiniz</option>
                      {customers.map((customer) => (
                        <option
                          key={customer.id}
                          value={customer.id}
                          title={customer.Başlık}
                        >
                          {truncateText(customer.Başlık)}
                        </option>
                      ))}
                    </select>
                    {isLoadingCustomers && (
                      <div className={styles.loadingText}>
                        Müşteriler yükleniyor...
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Projeler (Çoklu Seçim)</label>
                    {isLoadingProjects && (
                      <div className={styles.loadingText}>
                        Projeler yükleniyor...
                      </div>
                    )}
                    {!formData.customer ? (
                      <div className={styles.helperText}>
                        Önce müşteri seçiniz
                      </div>
                    ) : projects.length === 0 ? (
                      <div className={styles.helperText}>
                        Bu müşteri için proje bulunamadı
                      </div>
                    ) : (
                      <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid var(--input-border)',
                        borderRadius: '4px',
                        padding: '8px'
                      }}>
                        {projects.map((project) => {
                          const isSelected = formData.projects.includes(project.id);
                          const hasTariffs = projectsWithTariffs[project.id]?.length > 0;
                          return (
                            <div
                              key={project.id}
                              style={{
                                padding: '6px 8px',
                                marginBottom: '4px',
                                borderRadius: '4px',
                                backgroundColor: hasTariffs ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onClick={() => handleProjectToggle(project.id)}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleProjectToggle(project.id)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ flex: 1 }}>{project.titleShort}</span>
                              {hasTariffs && (
                                <span style={{
                                  fontSize: '0.75rem',
                                  color: '#ef4444',
                                  fontWeight: '600'
                                }}>
                                  Tarife Mevcut
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {formData.projects.length > 0 && (
                      <div className={styles.helperText}>
                        {formData.projects.length} proje seçildi
                      </div>
                    )}
                  </div>

                  {/* Project currency fix info */}
                  {formData.projects.length > 0 && (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        Seçili Projelerin Kur Bilgisi
                      </label>
                      <div className={styles.helperText}>
                        {formData.projects.map((projectId) => {
                          const project = projects.find((p) => p.id === projectId);
                          if (!project) return null;
                          const isFixed = !!project.isCurrencyFixed;
                          const rate = project.fixedCurrencyRate;
                          return (
                            <div key={projectId} style={{ marginBottom: '4px' }}>
                              <strong>{project.titleShort}:</strong>{' '}
                              {isFixed
                                ? `Kur sabitlemesi var (${rate})`
                                : 'Kur sabitlemesi yok'}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Tarife Tipi</label>
                    <select
                      value={formData.type}
                      onChange={handleTypeChange}
                      className={styles.select}
                      required
                    >
                      <option value="">Lütfen Seçiniz</option>
                      <option value="FTE">FTE</option>
                      <option value="Saat">Saat</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Döviz Tipi</label>
                    <select
                      value={formData.currency}
                      onChange={(e) =>
                        setFormData({ ...formData, currency: e.target.value })
                      }
                      className={styles.select}
                      required
                    >
                      <option value="">Lütfen Seçiniz</option>
                      <option value="TRY">TRY</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Tarih</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData({ ...formData, date: e.target.value })
                      }
                      className={styles.input}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Subcontractor?</label>
                    <select
                      value={formData.isSubcontractor}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isSubcontractor: e.target.value,
                        })
                      }
                      className={styles.select}
                      required
                    >
                      <option value="">Lütfen Seçiniz</option>
                      <option value="Evet">Evet</option>
                      <option value="Hayır">Hayır</option>
                    </select>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Bilgi</label>
                  <textarea
                    rows={3}
                    value={formData.info}
                    onChange={(e) =>
                      setFormData({ ...formData, info: e.target.value })
                    }
                    className={styles.textarea}
                  />
                </div>
              </div>

              <div className={styles.rightColumn}>
                <div className={styles.tariffValuesContainer}>
                  <label className={styles.label}>
                    {formData.type === 'Saat' ? 'Saat Ücreti' : 'FTE Değerleri'}
                  </label>

                  {formData.type === 'Saat' && (
                    <div className={styles.hourlyRateSection}>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>1 Saatlik Ücret</label>
                        <input
                          type="number"
                          value={formData.hourlyRate}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              hourlyRate: e.target.value,
                            })
                          }
                          className={styles.input}
                          required
                        />
                      </div>
                    </div>
                  )}

                  {formData.type === 'FTE' && (
                    <div className={styles.helperText}>
                      {selectedTariff
                        ? 'Mevcut FTE değerini kaldırmak için "Kaldır" butonuna tıklayabilirsiniz'
                        : 'Eklemek istediğiniz FTE değerlerini seçin'}
                    </div>
                  )}

                  {formData.type === 'FTE' && (
                    <div className={styles.defaultPriceSection}>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Varsayılan Fiyat</label>
                        <input
                          type="number"
                          value={defaultPrice}
                          onChange={handleDefaultPriceChange}
                          placeholder="Seçilen FTE değerleri için fiyat girin"
                          className={styles.input}
                        />
                        <div className={styles.helperText}>
                          Bu fiyat seçilen tüm FTE değerlerine uygulanacak, sonradan özel olarak düzenlenebilir
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.type === 'FTE' && (
                    <div className={styles.ftePercentageSection}>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Tarifelere Zam Yap (%)</label>
                        <div className={styles.percentageInputWrapper}>
                          <input
                            type="number"
                            placeholder="Örn: 10"
                            className={styles.percentageInput}
                            id="ftePercentageInput"
                          />
                          <button
                            type="button"
                            className={styles.applyPercentageButton}
                            onClick={() => {
                              const percentageInput = document.getElementById('ftePercentageInput');
                              const percentage = parseFloat(percentageInput.value) || 0;
                              
                              if (percentage === 0) {
                                toast.info('Lütfen bir artış yüzdesi girin');
                                return;
                              }

                              const selectedTariffs = tariffValues.filter(t => t.selected && t.price);
                              if (selectedTariffs.length === 0) {
                                toast.info('Fiyatı girili seçili tarife bulunamadı');
                                return;
                              }

                              const updatedTariffValues = tariffValues.map(tariff => {
                                if (tariff.selected && tariff.price) {
                                  const currentPrice = parseFloat(tariff.price) || 0;
                                  const newPrice = currentPrice + (currentPrice * percentage) / 100;
                                  return { ...tariff, price: newPrice.toFixed(2) };
                                }
                                return tariff;
                              });

                              setTariffValues(updatedTariffValues);
                              toast.success(`Seçili tarifelere %${percentage} zam uygulandı`);
                            }}
                          >
                            Uygula
                          </button>
                        </div>
                        <div className={styles.helperText}>
                          Seçili FTE değerlerinin fiyatlarına belirtilen yüzde kadar artış uygulamak için "Uygula" butonuna tıklayın
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.type === 'FTE' && (
                    <>
                      <div className={styles.tariffValuesGrid}>
                        {tariffValues.map((tariff, index) => {
                          const isExisting = tariff.existingId !== null;
                          return (
                            <div
                              key={tariff.value}
                              className={`${styles.tariffValueRow} ${
                                formData.type === 'FTE' && tariff.selected
                                  ? styles.selectedValue
                                  : ''
                              }`}
                            >
                              <div className={styles.tariffLabel}>
                                {`FTE ${tariff.value}`}
                              </div>
                              {isExisting && (
                                <span className={styles.existingLabel}>
                                  Mevcut
                                </span>
                              )}
                              <div className={styles.checkboxContainer}>
                                <input
                                  type="checkbox"
                                  checked={tariff.selected}
                                  onChange={() => handleTariffToggle(index)}
                                  className={styles.checkbox}
                                  id={`tariff-${tariff.value}`}
                                />
                                <label
                                  htmlFor={`tariff-${tariff.value}`}
                                  className={styles.checkboxLabel}
                                >
                                  {tariff.selected ? 'Seçildi' : 'Seçilmedi'}
                                </label>
                                {tariff.selected && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTariffToggle(index);
                                    }}
                                    className={styles.unselectButton}
                                    title="Bu değeri kaldırmak için tıklayın"
                                  >
                                    Kaldır
                                  </button>
                                )}
                              </div>
                              {tariff.selected && (
                                <div className={styles.priceInputContainer}>
                                  <input
                                    type="number"
                                    value={tariff.price}
                                    onChange={(e) => handleIndividualPriceChange(index, e.target.value)}
                                    placeholder="Fiyat"
                                    className={styles.priceInput}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className={styles.helperText}>
                        Seçmek veya kaldırmak için FTE değerlerine
                        tıklayabilirsiniz.
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {formData.type === 'Saat' && (
              <div className={styles.percentageSection}>
                <h3 className={styles.percentageSectionTitle}>
                  Ücret Artış Oranı
                </h3>
                <div className={styles.percentageInputContainer}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Artış Yüzdesi (%)</label>
                    <div className={styles.percentageInputWrapper}>
                      <input
                        type="number"
                        placeholder="Örn: 10"
                        className={styles.percentageInput}
                        onChange={(e) => {
                          const percentage = parseFloat(e.target.value) || 0;
                          // Store the entered percentage for later use
                          setFormData((prev) => ({
                            ...prev,
                            percentageIncrease: percentage,
                          }));
                        }}
                      />
                      <button
                        type="button"
                        className={styles.applyPercentageButton}
                        onClick={() => {
                          const percentage = formData.percentageIncrease || 0;
                          if (percentage <= 0) {
                            toast.info(
                              'Lütfen pozitif bir artış yüzdesi girin'
                            );
                            return;
                          }

                          const currentRate =
                            parseFloat(formData.hourlyRate) || 0;
                          if (currentRate <= 0) {
                            toast.info('Önce saatlik ücret girmelisiniz');
                            return;
                          }

                          // Calculate the new rate with the percentage increase
                          const newRate =
                            currentRate + (currentRate * percentage) / 100;
                          setFormData((prev) => ({
                            ...prev,
                            hourlyRate: newRate.toFixed(2),
                          }));

                          toast.success(
                            `Saatlik ücret %${percentage} oranında artırıldı`
                          );
                        }}
                      >
                        Uygula
                      </button>
                    </div>
                    <div className={styles.helperText}>
                      Mevcut saatlik ücrete belirtilen yüzde kadar artış
                      uygulamak için "Uygula" butonuna tıklayın
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div
              className={`${styles.buttonContainer} ${
                isDarkMode ? 'dark-mode' : ''
              }`}
            >
              <button
                type="button"
                onClick={handleModalClose}
                className={`${styles.button} ${styles.closeButton}`}
              >
                <FaTimes className={styles.buttonIcon} />
                Kapat
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                className={`${styles.button} ${styles.submitButton}`}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <FaSpinner
                      className={`${styles.buttonIcon} ${styles.spinnerIcon}`}
                    />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <FaSave className={styles.buttonIcon} />
                    {selectedTariff ? 'Güncelle' : 'Kaydet'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        {formData.projects.length > 0 && (
          <div className={styles.formContainer} style={{ marginTop: '12px' }}>
            <div className={styles.formGroup}>
              <h3 className={styles.label} style={{ marginBottom: '8px' }}>
                Seçili Projelere Ait Mevcut Tarifeler
              </h3>
              {isLoadingExistingTariffs ? (
                <div className={styles.loadingText}>
                  Tarifeler yükleniyor...
                </div>
              ) : existingTariffRecords && existingTariffRecords.length > 0 ? (
                <div className={styles.helperText}>
                  {formData.projects.map((projectId) => {
                    const project = projects.find((p) => p.id === projectId);
                    const projectTariffs = projectsWithTariffs[projectId] || [];

                    return (
                      <div key={projectId} style={{ marginBottom: '16px' }}>
                        <h4 style={{ marginBottom: '8px', fontWeight: '600' }}>
                          {project?.titleShort || projectId}
                        </h4>
                        {projectTariffs.length > 0 ? (
                          projectTariffs.map((t) => (
                            <div
                              key={t.id}
                              style={{
                                display: 'flex',
                                gap: '12px',
                                alignItems: 'center',
                                padding: '8px 10px',
                                border: '1px solid var(--input-border)',
                                borderRadius: '8px',
                                marginBottom: '8px',
                                backgroundColor: 'rgba(239, 68, 68, 0.05)',
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>{t.type}</span>
                              <span>| Değer: {t.value ?? '-'}</span>
                              <span>| Fiyat: {t.price ?? 0}</span>
                              <span>| Döviz: {t.currency ?? '-'}</span>
                              <span>| Tarih: {t.date ?? '-'}</span>
                              {t.active === false ? (
                                <span style={{ color: '#ef4444' }}>| Pasif</span>
                              ) : (
                                <span style={{ color: '#10b981' }}>| Aktif</span>
                              )}
                            </div>
                          ))
                        ) : (
                          <div style={{
                            padding: '8px',
                            fontStyle: 'italic',
                            color: '#10b981'
                          }}>
                            Bu projeye ait mevcut tarife yok (eklenebilir)
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.helperText}>
                  Seçili projelere ait mevcut tarife bulunamadı
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddTariffsModal;
